import type { JiraSprint } from '@ipen-portal/plugin-jira-common';

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

import { notEmpty } from './notEmpty/notEmpty';

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

export const cycleNameFormatter = (
  currentYear: number | string,
  currentQuarter: number,
) => {
  let formattedYear = currentYear;

  if (typeof currentYear === 'number') {
    formattedYear = (currentYear % 100).toString().padStart(2, '0');
  } else if (currentYear.length > 2) {
    formattedYear = currentYear.slice(-2);
  }

  return `${formattedYear}/Q${currentQuarter}`;
};

const extractCycle = (date: string) => {
  const quarter = Math.floor(dayjs(date).month() / 3) + 1;
  const year = dayjs(date).year().toString().slice(-2);

  return {
    quarter,
    year,
  };
};

export const generateCycles = (startDate: Date) => {
  const cycles = [];
  let startMoment = dayjs(startDate);
  const currentQuarter = Math.floor(dayjs().month() / 3) + 1;
  const currentYear = dayjs().year().toString().slice(-2);

  while (!(
    startMoment.year().toString().slice(-2) === currentYear
    && Math.floor(startMoment.month() / 3) + 1 === currentQuarter
  )) {
    const quarter = Math.floor(startMoment.month() / 3) + 1;
    const year = startMoment.year().toString().slice(-2);

    cycles.push(cycleNameFormatter(year, quarter));
    startMoment = startMoment.add(3, 'months');
  }

  cycles.push(cycleNameFormatter(currentYear, currentQuarter));
  return cycles;
};

export const getCycleByDate = (date: Date | string) => {
  const { quarter, year } = extractCycle(
    typeof date === 'string' ? date : date.toISOString(),
  );

  return cycleNameFormatter(year, quarter);
};

export const getCurrentCycle = () => {
  const now = dayjs().toISOString();

  const { quarter: currentQuarter, year: currentYear } = extractCycle(now);

  return cycleNameFormatter(currentYear, currentQuarter);
};

export const weeksOfQuarter = () => {
  const startOfQuarter = dayjs()
    .quarter(Math.floor(dayjs().month() / 3) + 1)
    .startOf('quarter');

  const endOfQuarter = dayjs()
    .quarter(Math.floor(dayjs().month() / 3) + 1)
    .endOf('quarter');

  const weeks: Date[] = [];
  let weekStart = startOfQuarter.clone().startOf('isoWeek');
  while (
    weekStart.isBefore(endOfQuarter)
    || weekStart.isSame(endOfQuarter, 'week')
  ) {
    weeks.push(weekStart.toDate());
    weekStart = weekStart.add(1, 'week');
  }
  return weeks;
};

export type Cycle = {
  name: string;
  quarter: number;
  year: number;
};

export const extractCycleFromSprint = (
  sprint: Pick<JiraSprint, 'name'>,
): Cycle | undefined => {
  const match = /^.*?(?<year>\d{2}|\d{4})\/Q(?<quarter>\d)$/i.exec(sprint.name);

  if (!match?.groups) {
    return undefined;
  }

  const { quarter, year } = match.groups;
  if (!year || !quarter) {
    return undefined;
  }

  const parsedYear = Number(year);

  return {
    name: `${year} / Q${quarter}`,
    quarter: Number(quarter),
    year: year.length === 2 ? 2000 + parsedYear : parsedYear,
  };
};

export const getLatestCycle = (sprints: Pick<JiraSprint, 'name'>[]) => {
  const cycles = sprints.map(extractCycleFromSprint).filter(notEmpty);
  return cycles.at(-1);
};
